import { LANE_GAP, MAX_ROADMAP_LANES, PHASE_BAR_HEIGHT, ROADMAP_ROW_HEIGHT } from "../constants";
import { GanttPackedPhaseBar, GanttPhaseBar, GanttPhaseKey } from "../models";

export const sortPhasesByStart = (phases: GanttPhaseBar[]): GanttPhaseBar[] =>
  [...phases].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

export const packPhaseLanes = (
  phases: GanttPhaseBar[],
  maxLanes: number = MAX_ROADMAP_LANES
): { bars: GanttPackedPhaseBar[]; laneCount: number } => {
  const laneEnds: number[] = [];

  const bars = sortPhasesByStart(phases).map((phase, colorIndex) => {
    const start = phase.startDate.getTime();
    const end = Math.max(phase.endDate.getTime(), start);

    const firstFree = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    const lane = Math.min(firstFree === -1 ? laneEnds.length : firstFree, maxLanes - 1);

    laneEnds[lane] = Math.max(laneEnds[lane] ?? end, end);

    return { ...phase, lane, colorIndex };
  });

  return { bars, laneCount: laneEnds.length };
};

export const getLaneStackHeight = (laneCount: number): number => {
  const lanes = Math.max(1, laneCount);

  return lanes * PHASE_BAR_HEIGHT + (lanes - 1) * LANE_GAP;
};

export const getRoadmapRowHeight = (laneCount: number): number =>
  ROADMAP_ROW_HEIGHT + (Math.max(1, laneCount) - 1) * (PHASE_BAR_HEIGHT + LANE_GAP);

export const getLaneTop = (lane: number): number =>
  (ROADMAP_ROW_HEIGHT - PHASE_BAR_HEIGHT) / 2 + lane * (PHASE_BAR_HEIGHT + LANE_GAP);

const clampRangesToNextStart = (phases: GanttPhaseBar[]): GanttPhaseBar[] => {
  const sorted = sortPhasesByStart(phases);

  return sorted.map((phase, i) => {
    const next = sorted[i + 1];
    if (next && next.startDate < phase.endDate) {
      return { ...phase, endDate: new Date(next.startDate) };
    }
    return phase;
  });
};

/**
 * Used to colour a milestone to match the phase it sits within.
 */
export const findPhaseKeyAtDate = (date: Date, phases: GanttPhaseBar[]): GanttPhaseKey | undefined => {
  const ranges = clampRangesToNextStart(phases);
  const match = ranges.find((p) => date >= p.startDate && date <= p.endDate);
  return match?.phase;
};

/**
 * Returns -1 when no stage contains the date.
 */
export const findStageIndexAtDate = (date: Date, phases: GanttPhaseBar[]): number => {
  const ranges = clampRangesToNextStart(phases);
  return ranges.findIndex((p) => date >= p.startDate && date <= p.endDate);
};
